import React, { useMemo } from 'react'

import {
  AppointmentSearchResult,
  DCNotification,
  NotificationPlacement,
  NotificationStyle,
  SimpleNotification,
} from '../../../__generated__/api'
import { useIsDesktop, useIsMobile } from '../../hooks/useBreakpoint'

import NotificationTile from './NotificationTile'
import SpecialistTile from './SpecialistTile'
import TimeslotTile from './TimeslotTile'
import { SpecialistTilesContainer, TimeslotTilesContainer } from './styles'
import { isDCNotification, isSimpleNotification } from './utils'

const TILES_BEFORE_NOTIFICATION = 2

interface Props {
  appointments: AppointmentSearchResult[]
  variant: 'specialist' | 'timeslot'
  hideSpecialistDetails?: 'hide-both' | 'hide-mobile' | 'false'
  onSpecialistClick?(appointment: AppointmentSearchResult): void
  onAppointmentClick?(appointment: AppointmentSearchResult): void
  remoteSupported: boolean
  notifications: Array<DCNotification | SimpleNotification>
  onDcNotificationClick(queue: string): void
  focusIndex?: number
}

const Tiles: React.FC<React.PropsWithChildren<Props>> = ({
  appointments,
  variant,
  hideSpecialistDetails,
  onSpecialistClick,
  onAppointmentClick,
  remoteSupported,
  notifications,
  onDcNotificationClick,
  focusIndex,
}) => {
  const isMobile = useIsMobile()
  const isDesktop = useIsDesktop()

  const sortedNotifications = useMemo(
    () => [
      ...notifications.filter((notification) => isDCNotification(notification)),
      ...notifications.filter(
        (notification) =>
          isSimpleNotification(notification) && notification.style === NotificationStyle.WalkIn
      ),
      ...notifications.filter(
        (notification) =>
          isSimpleNotification(notification) && notification.style !== NotificationStyle.WalkIn
      ),
    ],
    [notifications]
  )

  if (variant === 'specialist') {
    return (
      <SpecialistTilesContainer>
        {appointments.map((appointment, idx) => (
          <SpecialistTile
            key={`specialistTile-${appointment.appointmentId}-${idx}`}
            variant={isMobile ? 'S' : isDesktop ? 'L' : 'M'}
            appointment={appointment}
            onClick={onAppointmentClick ? () => onAppointmentClick(appointment) : undefined}
            onSpecialistClick={onSpecialistClick ? () => onSpecialistClick(appointment) : undefined}
            autoFocus={focusIndex === idx}
          />
        ))}
      </SpecialistTilesContainer>
    )
  }

  return (
    <TimeslotTilesContainer>
      {sortedNotifications
        .filter((notification) =>
          notification.placements.includes(NotificationPlacement.SearchResultsTop)
        )
        .map((notification) => (
          <NotificationTile
            key={`notification-${notification.id}`}
            notification={notification}
            onDCNotificationClick={onDcNotificationClick}
          />
        ))}
      {appointments.slice(0, TILES_BEFORE_NOTIFICATION).map((appointment) => (
        <TimeslotTile
          key={`timeslotTile-${appointment.appointmentId ?? appointment.assisDentSearchResult?.id}`}
          appointment={appointment}
          hideSpecialistDetails={hideSpecialistDetails}
          onClick={onAppointmentClick ? () => onAppointmentClick(appointment) : undefined}
          onSpecialistClick={onSpecialistClick ? () => onSpecialistClick(appointment) : undefined}
          remoteSupported={remoteSupported}
        />
      ))}
      {sortedNotifications
        .filter((notification) =>
          notification.placements.includes(NotificationPlacement.SearchResultsWithin)
        )
        .map((notification) => (
          <NotificationTile
            key={`notification-${notification.id}`}
            notification={notification}
            onDCNotificationClick={onDcNotificationClick}
          />
        ))}
      {appointments.slice(TILES_BEFORE_NOTIFICATION).map((appointment, index) => (
        <TimeslotTile
          key={`timeslotTile-${appointment.appointmentId ?? appointment.assisDentSearchResult?.id}`}
          appointment={appointment}
          hideSpecialistDetails={hideSpecialistDetails}
          onClick={onAppointmentClick ? () => onAppointmentClick(appointment) : undefined}
          onSpecialistClick={onSpecialistClick ? () => onSpecialistClick(appointment) : undefined}
          remoteSupported={remoteSupported}
          autoFocus={index + TILES_BEFORE_NOTIFICATION === focusIndex}
        />
      ))}
    </TimeslotTilesContainer>
  )
}

export default Tiles
